import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Антология — лицензии на ПО и AI';

const removeMetaByAttr = (attr, value) => {
  document.querySelectorAll(`meta[${attr}="${value}"]`).forEach((el) => el.remove());
};

const removeCanonicalLinks = () => {
  document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
};

const removeJsonLdScripts = () => {
  document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
};

/**
 * Компонент для управления SEO мета-тегами (Yandex, Google, OG, AI-краулеры).
 */
const SEOHead = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false,
  canonical,
  publishedTime,
  modifiedTime,
}) => {
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://antologia.tech';
  const fullUrl = url || `${baseUrl}${location.pathname}`;
  const fullImage = image
    ? image.startsWith('http')
      ? image
      : `${baseUrl}${image}`
    : `${baseUrl}/og-image.jpg`;

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    removeMetaByAttr('name', 'description');
    removeMetaByAttr('name', 'keywords');
    removeMetaByAttr('name', 'robots');
    removeMetaByAttr('property', 'og:title');
    removeMetaByAttr('property', 'og:description');
    removeMetaByAttr('property', 'og:image');
    removeMetaByAttr('property', 'og:url');
    removeMetaByAttr('property', 'og:type');
    removeMetaByAttr('property', 'og:site_name');
    removeMetaByAttr('property', 'og:locale');
    removeMetaByAttr('property', 'article:published_time');
    removeMetaByAttr('property', 'article:modified_time');
    removeMetaByAttr('name', 'twitter:card');
    removeMetaByAttr('name', 'twitter:title');
    removeMetaByAttr('name', 'twitter:description');
    removeMetaByAttr('name', 'twitter:image');

    const createMetaTag = (name, content, attribute = 'name') => {
      if (!content) return;
      const meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    if (description) {
      createMetaTag('description', description);
    }
    if (keywords) {
      createMetaTag('keywords', keywords);
    }
    createMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    if (title) {
      createMetaTag('og:title', title, 'property');
    }
    if (description) {
      createMetaTag('og:description', description, 'property');
    }
    createMetaTag('og:image', fullImage, 'property');
    createMetaTag('og:url', fullUrl, 'property');
    createMetaTag('og:type', type, 'property');
    createMetaTag('og:site_name', SITE_NAME, 'property');
    createMetaTag('og:locale', 'ru_RU', 'property');

    if (type === 'article') {
      if (publishedTime) {
        createMetaTag('article:published_time', publishedTime, 'property');
      }
      if (modifiedTime) {
        createMetaTag('article:modified_time', modifiedTime, 'property');
      }
    }

    createMetaTag('twitter:card', 'summary_large_image');
    if (title) {
      createMetaTag('twitter:title', title);
    }
    if (description) {
      createMetaTag('twitter:description', description);
    }
    createMetaTag('twitter:image', fullImage);

    const yandexVerification = process.env.REACT_APP_YANDEX_VERIFICATION;
    if (yandexVerification) {
      createMetaTag('yandex-verification', yandexVerification);
    }

    removeCanonicalLinks();
    const canonicalUrl = canonical || fullUrl;
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonicalUrl);
    document.head.appendChild(link);

    removeJsonLdScripts();
    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    return () => {
      removeJsonLdScripts();
      removeCanonicalLinks();
    };
  }, [
    title,
    description,
    keywords,
    image,
    url,
    type,
    structuredData,
    noindex,
    canonical,
    fullUrl,
    fullImage,
    publishedTime,
    modifiedTime,
  ]);

  return null;
};

export default SEOHead;
